// app/frontend/components/ui/Tabs/L2KTabs.tsx
import { Tabs, Group } from "@mantine/core";
import { L2KBadge } from "../Typography";
import { ReactNode } from "react";
import { COLORS } from "../Typography/constants";

export interface L2KTabItem {
  value: string;
  label: string;
  icon?: ReactNode;
  count?: number;
}

export interface L2KTabsProps {
  tabs: L2KTabItem[];
  value: string | null;
  onChange: (value: string | null) => void;
  children: ReactNode;
  grow?: boolean;
}

/**
 * L2KTabs - Branded tabs component with optional icons and badge counts
 * Matches the Learntokiz design system with orange accent and dark theme
 */
export function L2KTabs({
  tabs,
  value,
  onChange,
  children,
  grow = true,
}: L2KTabsProps) {
  return (
    <Tabs
      value={value}
      onChange={onChange}
      variant="pills"
      styles={{
        root: {
          backgroundColor: "transparent",
        },
        list: {
          backgroundColor: "rgba(255, 255, 255, 0.05)",
          padding: "4px",
          borderRadius: "8px",
          gap: "4px",
          border: "1px solid rgba(255, 255, 255, 0.1)",
        },
        tab: {
          fontFamily: "'Public Sans', sans-serif",
          fontWeight: 600,
          fontSize: "14px",
          color: COLORS.primary,
          transition: "all 0.2s ease",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          "&[data-active]": {
            backgroundColor: COLORS.accentDark,
            color: "#171717",
          },
          "&:hover:not([data-active])": {
            backgroundColor: "rgba(255, 255, 255, 0.08)",
            color: COLORS.primary,
            fontWeight: 500,
          },
        },
      }}
    >
      <Tabs.List grow={grow}>
        {tabs.map((tab) => (
          <Tabs.Tab key={tab.value} value={tab.value} leftSection={tab.icon}>
            <Group gap={8} justify="center">
              <span>{tab.label}</span>
              {tab.count !== undefined && (
                <L2KBadge
                  size="sm"
                  radius="xl"
                  styles={{
                    root: {
                      backgroundColor:
                        value === tab.value
                          ? "rgba(0, 0, 0, 0.2)"
                          : "rgba(255, 153, 0, 0.08)",
                      color: value === tab.value ? "#171717" : COLORS.accent,
                      fontWeight: 600,
                      minWidth: "24px",
                      height: "20px",
                      padding: "0 6px",
                    },
                  }}
                >
                  {tab.count}
                </L2KBadge>
              )}
            </Group>
          </Tabs.Tab>
        ))}
      </Tabs.List>
      {children}
    </Tabs>
  );
}

/**
 * L2KTabsPanel - Wrapper for Tabs.Panel with consistent styling
 */
export function L2KTabsPanel({
  value,
  children,
  pt = "xl",
}: {
  value: string;
  children: ReactNode;
  pt?: string;
}) {
  return (
    <Tabs.Panel value={value} pt={pt}>
      {children}
    </Tabs.Panel>
  );
}

export default L2KTabs;
